import { BackgroundBeams } from '@/components/ui/background-beams'
import { AuthService } from '@/services/auth/auth_service'
import { Button, Card, message, Typography, Form, Input, Modal, theme } from 'antd'
import { useState } from 'react';

const { Title } = Typography

export default function LoginScreen() {

    const { token } = theme.useToken();
    const [loading, setLoading] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);

    function getErrorCode(error: unknown): string | undefined {
        if (error && typeof error === 'object' && 'code' in error) {
            const code = (error as { code: unknown }).code;
            return typeof code === 'string' ? code : undefined;
        }
        return undefined;
    }

    function getFirebaseErrorMessage(errorCode: string): string {
        switch (errorCode) {
            case 'auth/user-not-found':
                return 'Kein Benutzer mit dieser E-Mail-Adresse gefunden.';
            case 'auth/wrong-password':
                return 'Das eingegebene Passwort ist falsch.';
            case 'auth/invalid-email':
                return 'Die E-Mail-Adresse ist ungültig.';
            case 'auth/user-disabled':
                return 'Dieses Benutzerkonto wurde deaktiviert.';
            case 'auth/too-many-requests':
                return 'Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuchen Sie es später erneut.';
            case 'auth/email-already-in-use':
                return 'Diese E-Mail-Adresse wird bereits verwendet.';
            case 'auth/weak-password':
                return 'Das Passwort ist zu schwach. Verwenden Sie mindestens 6 Zeichen.';
            case 'auth/network-request-failed':
                return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
            case 'auth/invalid-credential':
                return 'Die Anmeldedaten sind ungültig. Bitte überprüfen Sie E-Mail und Passwort.';
            case 'auth/popup-closed-by-user':
                return 'Das Anmeldefenster wurde geschlossen.';
            case 'auth/cancelled-popup-request':
                return 'Die Anmeldung wurde abgebrochen.';
            default:
                return 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
        }
    }

    async function handleSAMLLogin() {
        setLoading(true);
        try {
            await AuthService.getInstance().signInWithSAML();
        } catch (error) {
            console.error(error);
            const errorCode = getErrorCode(error);
            const errorMessage = errorCode ? getFirebaseErrorMessage(errorCode) : 'Microsoft-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.';
            message.error({
                content: errorMessage,
                duration: 6,
            });
        }
        setLoading(false);
    }

    async function handleEmailLogin(values: { email: string; password: string }) {
        setLoading(true);
        try {
            await AuthService.getInstance().signInWithEmailAndPassword(values.email, values.password);
        } catch (error) {
            console.error(error);
            const errorCode = getErrorCode(error);
            const errorMessage = errorCode ? getFirebaseErrorMessage(errorCode) : 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.';
            message.error({
                content: errorMessage,
                duration: 6,
            });
        }
        setLoading(false);
    }

    async function handlePasswordReset(values: { email: string }) {
        setLoading(true);
        try {
            await AuthService.getInstance().sendPasswordResetEmail(values.email);
            message.success({
                content: 'Eine E-Mail zum Zurücksetzen des Passworts wurde an Ihre E-Mail-Adresse gesendet. Bitte überprüfen Sie auch Ihren Spam-Ordner.',
                duration: 8,
            });
            setResetModalOpen(false);
        } catch (error) {
            console.error(error);
            const errorCode = getErrorCode(error);
            const errorMessage = errorCode ? getFirebaseErrorMessage(errorCode) : 'Passwort-Reset fehlgeschlagen. Bitte versuchen Sie es erneut.';
            message.error({
                content: errorMessage,
                duration: 6,
            });
        }
        setLoading(false);
    }

    return (
        <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "hsl(222.2, 84%, 4.9%)" }}>
            <BackgroundBeams />
            <Card style={{ width: 450, margin: "0 auto" }}>
                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    marginBottom: 15
                }}>
                    <img src='/assets/logo_full.png' width={250} />
                </div>
                <div className='text-center'>
                    <Title level={1}>Willkommen!</Title>
                </div>

                <div style={{ marginTop: 24 }}>
                    <Form onFinish={handleEmailLogin} layout="vertical" size="large">
                        <Form.Item
                            name="email"
                            label="E-Mail"
                            rules={[
                                { required: true, message: 'Bitte geben Sie Ihre E-Mail ein!' },
                                { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail ein!' }
                            ]}
                            style={{ marginBottom: 16 }}
                        >
                            <Input
                                placeholder="ihre.email@beispiel.de"
                                style={{
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    fontSize: '16px'
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Passwort"
                            rules={[{ required: true, message: 'Bitte geben Sie Ihr Passwort ein!' }]}
                            style={{ marginBottom: 8 }}
                        >
                            <Input.Password
                                placeholder="Ihr Passwort"
                                style={{
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    fontSize: '16px'
                                }}
                            />
                        </Form.Item>
                        <div className='text-right' style={{ marginBottom: 20 }}>
                            <Button
                                type="link"
                                onClick={() => setResetModalOpen(true)}
                                style={{ padding: 0, fontSize: '14px', color: token.colorTextSecondary }}
                            >
                                Passwort vergessen?
                            </Button>
                        </div>
                        <Form.Item style={{ marginBottom: 20 }}>
                            <Button
                                type='primary'
                                htmlType="submit"
                                loading={loading}
                                style={{
                                    width: '100%',
                                    height: '48px',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '500'
                                }}
                            >
                                Anmelden
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        margin: '24px 0',
                        color: token.colorTextQuaternary
                    }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: token.colorBorderSecondary }}></div>
                        <span style={{ padding: '0 16px', fontSize: '14px' }}>oder</span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: token.colorBorderSecondary }}></div>
                    </div>

                    <Button
                        onClick={handleSAMLLogin}
                        loading={loading}
                        style={{
                            width: '100%',
                            height: '48px',
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorText,
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 23 23" fill="#00BCF2" style={{ marginRight: '12px' }}>
                            <path d="M11 0H0v11h11V0z" fill="#F25022" />
                            <path d="M23 0H12v11h11V0z" fill="#7FBA00" />
                            <path d="M11 12H0v23h11V12z" fill="#00A4EF" />
                            <path d="M23 12H12v23h11V12z" fill="#FFB900" />
                        </svg>
                        Mit Microsoft anmelden
                    </Button>
                </div>

                <Modal
                    title="Passwort zurücksetzen"
                    open={resetModalOpen}
                    onCancel={() => setResetModalOpen(false)}
                    footer={null}
                >
                    <Form onFinish={handlePasswordReset} layout="vertical">
                        <Form.Item
                            name="email"
                            label="E-Mail"
                            rules={[
                                { required: true, message: 'Bitte geben Sie Ihre E-Mail ein!' },
                                { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail ein!' }
                            ]}
                        >
                            <Input placeholder="ihre.email@beispiel.de" />
                        </Form.Item>
                        <Form.Item>
                            <Button
                                type='primary'
                                htmlType="submit"
                                className='w-full'
                                loading={loading}
                            >
                                Reset-E-Mail senden
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </Card>
        </div>
    )
}
